import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import MUIProvider from '~/contexts/MUIProvider';
import { Link } from '~/lib/react-router-dom';

import InfoIcon from '@mui/icons-material/Info';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import MarkdownPreview from '~/components/Common/MarkdownPreview';
import Spinner from '~/components/Common/Spinner';

import { formatDateISO } from '~/services/TimeService';
import DealCrawlerService, { CruiseDealCrawler, CruiseDealSailingCrawler } from '~/services/cruises/DealCrawlerService';

import {
  DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH,
  buildDealCrawlerPageCreationSearch,
} from './DealCrawlerPageCreation/paths';

export const getCrawlerStatusColor = (status: string) => {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'error';
    default:
      return 'warning';
  }
};

export default function DealCrawlerView() {
  const { id } = useParams<{ id: string }>();

  const [dealCrawler, setDealCrawler] = useState<CruiseDealCrawler | null>(null);
  const [sailings, setSailings] = useState<Array<CruiseDealSailingCrawler>>([]);
  const [isLoadingCrawler, setIsLoadingCrawler] = useState(false);
  const [isLoadingSailings, setIsLoadingSailings] = useState(false);
  const [isRestartingCrawler, setIsRestartingCrawler] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const fetchDealCrawler = useCallback(async () => {
    try {
      setIsLoadingCrawler(true);
      const response = await DealCrawlerService.getById(id);
      setDealCrawler(response.result);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Error fetching deal crawler', { variant: 'error' });
    } finally {
      setIsLoadingCrawler(false);
    }
  }, [id, enqueueSnackbar]);

  const fetchSailings = useCallback(async () => {
    try {
      setIsLoadingSailings(true);
      const response = await DealCrawlerService.getSailings(id);
      setSailings(response.result);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Error fetching sailings', { variant: 'error' });
    } finally {
      setIsLoadingSailings(false);
    }
  }, [id, enqueueSnackbar]);

  const restartCrawler = useCallback(async () => {
    try {
      setIsRestartingCrawler(true);
      await DealCrawlerService.crawlDealPage(dealCrawler.sourceUrl);
      enqueueSnackbar('Crawler restarted successfully', { variant: 'success' });

      fetchSailings();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Error restarting crawler', { variant: 'error' });
    } finally {
      setIsRestartingCrawler(false);
    }
  }, [dealCrawler, enqueueSnackbar, fetchSailings]);

  const allPromotions = useMemo(() => {
    const promotions = sailings.flatMap(
      (sailingCrawler) => sailingCrawler.rates?.flatMap((rate) => rate.promotions) || [],
    );

    return [...new Set(promotions)];
  }, [sailings]);

  const allRateCodes = useMemo(() => {
    const rateCodes = sailings.flatMap((sailingCrawler) => sailingCrawler.rates?.map((rate) => rate.rateCode) || []);

    return [...new Set(rateCodes)];
  }, [sailings]);

  useEffect(() => {
    fetchDealCrawler();
    fetchSailings();
  }, [fetchDealCrawler, fetchSailings]);

  const canRestartCrawler = useMemo(() => {
    const hasFailedSailings = sailings.some((sailingCrawler) => sailingCrawler.status === 'FAILED');
    const hasPendingSailings = sailings.some((sailingCrawler) => sailingCrawler.status === 'PENDING');

    return hasFailedSailings && !hasPendingSailings;
  }, [sailings]);

  return (
    <MUIProvider>
      <Helmet>
        <title>Deal crawler</title>
      </Helmet>

      <Container maxWidth="xl">
        <PageHeader title="Deal crawler" backButton />

        {dealCrawler && (
          <Stack direction="column" spacing={4}>
            <Card variant="outlined">
              <CardHeader
                title={
                  <Typography variant="h4" fontWeight="bold">
                    {dealCrawler.dealPageName}
                  </Typography>
                }
                action={
                  <Stack direction="column" spacing={1} alignItems="end">
                    <Button
                      variant="contained"
                      color="primary"
                      component={Link}
                      disabled={!allRateCodes.length}
                      to={`${DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH}?${buildDealCrawlerPageCreationSearch({
                        crawlerIds: [dealCrawler.id],
                        mainCrawlerId: dealCrawler.id,
                      })}`}
                    >
                      Create Deal Page
                    </Button>

                    {!allRateCodes.length && !!dealCrawler.sailingsByStatus.PENDING && (
                      <Tooltip title="No rate codes found, waiting for sailings to finish crawling">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <InfoIcon fontSize="small" color="warning" />
                          <Typography variant="body1" fontWeight="bold">
                            Pending sailings: {dealCrawler.sailingsByStatus.PENDING}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    )}

                    {!allRateCodes.length && !dealCrawler.sailingsByStatus.PENDING && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <InfoIcon fontSize="small" color="error" />
                        <Typography variant="body1" fontWeight="bold">
                          No rate codes found
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                }
              />

              <CardContent>
                <Stack direction="column" spacing={2}>
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body1" fontWeight="bold">
                      Source URL:
                    </Typography>

                    <Typography variant="body1">{dealCrawler.sourceUrl}</Typography>

                    <a href={dealCrawler.sourceUrl} target="_blank" rel="noopener noreferrer">
                      <Tooltip title="View source page">
                        <OpenInNewIcon fontSize="small" color="primary" />
                      </Tooltip>
                    </a>
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Typography variant="body1" fontWeight="bold">
                      Vendor:
                    </Typography>

                    <Typography variant="body1">{dealCrawler.vendor.name}</Typography>
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Typography variant="body1" fontWeight="bold">
                      Due date:
                    </Typography>
                    <Typography variant="body1">
                      {dealCrawler.dueDate ? formatDateISO(dealCrawler.dueDate) : 'N/A'}
                    </Typography>
                  </Stack>

                  <Stack direction="column">
                    <Typography variant="body1" fontWeight="bold">
                      Description:
                    </Typography>
                    <Box border={1} borderColor="divider" borderRadius={1} p={1}>
                      <MarkdownPreview content={dealCrawler.dealPageDescription} />
                    </Box>
                  </Stack>

                  {allPromotions.length > 0 && (
                    <Stack direction="column">
                      <Typography variant="body1" fontWeight="bold">
                        Promotions:
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        rowGap={1}
                        flexWrap="wrap"
                        border={1}
                        borderColor="divider"
                        borderRadius={1}
                        p={1}
                      >
                        {allPromotions.map((promotion) => (
                          <Chip key={promotion} label={promotion} size="small" color="info" />
                        ))}
                      </Stack>
                    </Stack>
                  )}

                  {allRateCodes.length > 0 && (
                    <Stack direction="column">
                      <Typography variant="body1" fontWeight="bold">
                        Rate codes:
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        rowGap={1}
                        flexWrap="wrap"
                        border={1}
                        borderColor="divider"
                        borderRadius={1}
                        p={1}
                      >
                        {allRateCodes.map((rateCode) => (
                          <Chip key={rateCode} label={rateCode} size="small" color="info" />
                        ))}
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Box>
              {isLoadingSailings && (
                <Stack direction="row" justifyContent="center" alignItems="center" height="100%">
                  <Spinner />
                </Stack>
              )}

              {sailings.length > 0 && (
                <Stack direction="column" spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h5" fontWeight="bold" color="text.primary">
                        Sailings ({sailings.length})
                      </Typography>

                      <Stack direction="row" spacing={1} alignItems="center">
                        {!!dealCrawler.sailingsByStatus.PENDING && (
                          <Tooltip title="Pending sailings">
                            <Chip
                              label={`Pending: ${dealCrawler.sailingsByStatus.PENDING}`}
                              color="warning"
                              size="small"
                            />
                          </Tooltip>
                        )}
                        {!!dealCrawler.sailingsByStatus.FAILED && (
                          <Tooltip title="Failed sailings">
                            <Chip label={`Failed: ${dealCrawler.sailingsByStatus.FAILED}`} color="error" size="small" />
                          </Tooltip>
                        )}
                        {!!dealCrawler.sailingsByStatus.COMPLETED && (
                          <Tooltip title="Completed sailings">
                            <Chip
                              label={`Completed: ${dealCrawler.sailingsByStatus.COMPLETED}`}
                              color="success"
                              size="small"
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>

                    {canRestartCrawler && (
                      <Button
                        variant="contained"
                        color="info"
                        size="small"
                        onClick={restartCrawler}
                        disabled={isRestartingCrawler}
                      >
                        {isRestartingCrawler ? <Spinner size={20} /> : 'Restart crawler'}
                      </Button>
                    )}
                  </Stack>

                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table stickyHeader aria-label="sticky table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Sailing Name</TableCell>
                          <TableCell>Sailing Status</TableCell>
                          <TableCell>Crawler Status</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {sailings.map((sailingCrawler) => (
                          <TableRow key={sailingCrawler.id}>
                            <TableCell>
                              <Typography variant="body1" fontWeight="bold">
                                {sailingCrawler.departure.name}
                                <Link to={`/cruises/sailings/${sailingCrawler.departure.id}`}>
                                  <Tooltip title="View sailing page">
                                    <OpenInNewIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
                                  </Tooltip>
                                </Link>
                              </Typography>
                            </TableCell>
                            <TableCell>{sailingCrawler.departure.status}</TableCell>
                            <TableCell>
                              <Tooltip title={sailingCrawler.error}>
                                <Chip
                                  label={sailingCrawler.status}
                                  color={getCrawlerStatusColor(sailingCrawler.status)}
                                  size="small"
                                />
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              )}
            </Box>
          </Stack>
        )}

        {isLoadingCrawler && (
          <Stack direction="row" justifyContent="center" alignItems="center" height="100%">
            <Spinner />
          </Stack>
        )}
      </Container>
    </MUIProvider>
  );
}
